'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Modal } from '@common/components';
import { useToast } from '@common/hooks';
import { cn, isEdge } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { useState, useTransition } from 'react';
import { AuthQueryOptions, VERIFICATION_TYPE } from '@/api';
import type { GiftProductDetail } from '@/api/getGiftProductDetail';
import { buildGiftOrderSearchParams, hasAllRequiredOptions } from '@/app/donation/_utils/giftOrder';
import { GIFT_SALE_STATUS, GIFT_SALE_STATUS_LABEL } from '@/app/donation/constants';
import type { GiftSaleStatus } from '@/app/donation/types';
import { DONATION_ROUTES, ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useLoginGuard } from '@/hooks/useLoginGuard';
import { useOverlayHistory } from '@/hooks/useOverlayHistory';
import { useAuthStore } from '@/stores/AuthStore';
import { calculateGiftDonationAmount } from '../_utils/calculateGiftDonationAmount';
export interface SelectedGiftOption {
  optionGroupId: number;
  optionGroupName: string;
  optionItemId: number;
  optionItemName: string;
  extraProductPrice: number;
}
interface GiftDonateButtonProps {
  productInfo: GiftProductDetail;
  productNumber: string;
  selectedOptions?: SelectedGiftOption[];
  hasOptions?: boolean;
  customButtonText?: string;
  className?: string;
  dimensions?: 'H56' | 'H48' | 'H40';
  theme?: 'primary-fill' | 'sub-line' | 'sub-fill';
  requiredOptionGroupIds?: number[];
  onOptionNotSelected?: () => void;
  /** 주문 수량 */
  quantity?: number;
}
const SELECT_OPTION_MESSAGE = '답례품 옵션을 선택해주세요';
type ModalType = 'NONE' | 'EDGE';
export const GiftDonateButton = t0 => {
  const $ = _c(32);
  const {
    productInfo,
    productNumber,
    selectedOptions: t1,
    hasOptions: t2,
    customButtonText,
    className,
    dimensions: t3,
    theme: t4,
    requiredOptionGroupIds: t5,
    onOptionNotSelected,
    quantity: t6
  } = t0;
  const selectedOptions = t1 === undefined ? [] : t1;
  const hasOptions = t2 === undefined ? false : t2;
  const dimensions = t3 === undefined ? "H56" : t3;
  const theme = t4 === undefined ? "primary-fill" : t4;
  let t7;
  if ($[0] !== t5) {
    t7 = t5 === undefined ? [] : t5;
    $[0] = t5;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const requiredOptionGroupIds = t7;
  const quantity = t6 === undefined ? 1 : t6;
  const router = useCustomRouter();
  const pathname = usePathname();
  const loginGuard = useLoginGuard();
  const {
    toast
  } = useToast();
  const [modalType, setModalType] = useState("NONE");
  const [isNavigating, startTransition] = useTransition();
  let t8;
  if ($[2] !== setModalType) {
    t8 = () => setModalType("NONE");
    $[2] = setModalType;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  useOverlayHistory(modalType !== "NONE", t8);
  const isLogin = useAuthStore(_temp);
  let t9;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = AuthQueryOptions["post:/wello/v2/auth/member-verification/info"]();
    $[4] = t9;
  } else {
    t9 = $[4];
  }
  let t10;
  if ($[5] !== isLogin) {
    t10 = {
      ...t9,
      enabled: isLogin
    };
    $[5] = isLogin;
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  const verificationInfoQuery = useQuery(t10);
  const isDisabled = productInfo.productSaleStatus !== GIFT_SALE_STATUS.ON_SALE;
  const donationAmount = calculateGiftDonationAmount({
    currentPrice: productInfo.currentPrice,
    extraProductPrices: selectedOptions.map(_temp2),
    quantity,
    hasOptions
  });
  const getButtonText = () => {
    if (customButtonText) {
      return customButtonText;
    }
    if (isDisabled && productInfo.productSaleStatus) {
      return GIFT_SALE_STATUS_LABEL[productInfo.productSaleStatus as GiftSaleStatus];
    }
    return `${donationAmount.toLocaleString("ko-KR")}원 기부하기`;
  };
  const navigateToOrder = () => {
    startTransition(() => {
      router.push(DONATION_ROUTES.HOMETOWN_BUSINESS_ORDER.withSearchParams({
        searchParams: buildGiftOrderSearchParams({
          institutionId: productInfo.institutionId,
          institutionName: productInfo.institutionName,
          productNumber,
          optionItemIds: hasOptions ? selectedOptions.map(_temp3) : [],
          quantity
        })
      }));
    });
  };
  const handleDonate = () => {
    if (isEdge()) {
      setModalType("EDGE");
      return;
    }
    const isMissingRequiredOption = hasOptions && !hasAllRequiredOptions(selectedOptions.map(_temp4), requiredOptionGroupIds);
    if (isMissingRequiredOption) {
      toast({
        message: SELECT_OPTION_MESSAGE,
        type: "info",
        offset: {
          bottom: 80
        }
      });
      onOptionNotSelected?.();
      return;
    }
    if (!loginGuard()) {
      return;
    }
    const isCi = !!verificationInfoQuery.data?.context?.ci;
    if (!isCi) {
      startTransition(() => {
        router.push(ROUTES.EASY_CERTIFICATION.withSearchParams({
          searchParams: {
            verification: VERIFICATION_TYPE.PROFILE_UPDATE,
            redirect: pathname ?? ""
          }
        }));
      });
      return;
    }
    navigateToOrder();
  };
  const T0 = Button;
  const t11 = isDisabled && "border-Gray-100 bg-Gray-100 text-Gray-400";
  let t12;
  if ($[7] !== className || $[8] !== t11) {
    t12 = cn(className, t11);
    $[7] = className;
    $[8] = t11;
    $[9] = t12;
  } else {
    t12 = $[9];
  }
  const t13 = getButtonText();
  let t14;
  if ($[10] !== T0 || $[11] !== dimensions || $[12] !== handleDonate || $[13] !== isDisabled || $[14] !== isNavigating || $[15] !== t12 || $[16] !== t13 || $[17] !== theme) {
    t14 = <T0 className={t12} dimensions={dimensions} theme={theme} loading={isNavigating} onClick={handleDonate} disabled={isDisabled}>{t13}</T0>;
    $[10] = T0;
    $[11] = dimensions;
    $[12] = handleDonate;
    $[13] = isDisabled;
    $[14] = isNavigating;
    $[15] = t12;
    $[16] = t13;
    $[17] = theme;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  const t15 = modalType === "EDGE";
  let t16;
  if ($[19] !== setModalType) {
    t16 = () => setModalType("NONE");
    $[19] = setModalType;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  let t18;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <h2 className="font-700 text-Gray-900 typo-body2">웰로의 고향사랑기부제는 엣지 브라우저에서 지원되지 않습니다.</h2>;
    t18 = <p className="font-400 text-Gray-700 typo-body3">원활한 이용을 위해 Chrome, Safari 등의 브라우저를 이용해 주세요.</p>;
    $[21] = t17;
    $[22] = t18;
  } else {
    t17 = $[21];
    t18 = $[22];
  }
  let t19;
  if ($[23] !== setModalType) {
    t19 = <div className="flex flex-col gap-16 text-center">{t17}{t18}<Button onClick={() => setModalType("NONE")}>확인</Button></div>;
    $[23] = setModalType;
    $[24] = t19;
  } else {
    t19 = $[24];
  }
  let t20;
  if ($[25] !== t15 || $[26] !== t16 || $[27] !== t19) {
    t20 = <Modal opened={t15} onClose={t16}>{t19}</Modal>;
    $[25] = t15;
    $[26] = t16;
    $[27] = t19;
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  let t21;
  if ($[29] !== t14 || $[30] !== t20) {
    t21 = <>{t14}{t20}</>;
    $[29] = t14;
    $[30] = t20;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  return t21;
};
function _temp(state) {
  return !!state.isLogin;
}
function _temp2(option) {
  return option.extraProductPrice;
}
function _temp3(option_0) {
  return option_0.optionItemId;
}
function _temp4(option_1) {
  return option_1.optionGroupId;
}